import { addDays, format } from '@if/core/date'
import { EApplicantType, EDocumentStatus } from '@if/core/types'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

export function useDocumentStatus(documentRequired: RequiredDocument) {
  const { t } = useTranslation()

  return useMemo(() => {
    let statusMessage: string
    let chipColor: ChipColor

    switch (documentRequired.status) {
      case EDocumentStatus.Approved:
        statusMessage = t('EDocumentStatus.approved', { ns: 'enum' })
        chipColor = 'success'
        break
      case EDocumentStatus.AwaitingApproval:
        statusMessage = t('EDocumentStatus.awaitingApproval', { ns: 'enum' })
        chipColor = 'info'
        break
      case EDocumentStatus.Refused:
        statusMessage = t('EDocumentStatus.refused', { ns: 'enum' })
        chipColor = 'error'
        break
      case EDocumentStatus.Incomplete:
        statusMessage = t('EDocumentStatus.incomplete', { ns: 'enum' })
        chipColor = 'warning'
        break
      default:
        statusMessage = t('viewCreditApplication.documentsAwaitingUpload')
        chipColor = 'warning'
    }
    return { statusMessage, chipColor }
  }, [documentRequired.status, t])
}

export function useFullNameText(documentRequired: RequiredDocument) {
  const { t } = useTranslation()

  const fullNameText = useMemo(() => {
    let applicantSuffix = ''
    if (documentRequired.applicantType === EApplicantType.Applicant) {
      applicantSuffix = ` - ${t('common.applicant')}`
    } else if (documentRequired.applicantType === EApplicantType.CoApplicant) {
      applicantSuffix = ` - ${t('common.coApplicant')}`
    }

    return t('common.currentLanguage') === 'Fr'
      ? `${documentRequired.nameFr}${applicantSuffix}`
      : `${documentRequired.nameEn}${applicantSuffix}`
  }, [documentRequired, t])

  return fullNameText
}

export function canSendOrDeleteHighEstimateInvoice(firstPaymentOn: Date) {
  const maxDateToSendInvoice = format(addDays(firstPaymentOn, -5), 'yyyy-MM-dd')
  const today = format(new Date(), 'yyyy-MM-dd')
  return today <= maxDateToSendInvoice
}
